// NodeViewer.js
import React from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Tooltip,
  Alert,
  AlertTitle,
  Chip,
} from "@mui/material";
import { buildNumberedList } from "./nodeUtils";

const safeText = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const TONES = {
  pass: {
    label: "Pass",
    border: "#2e7d32",
    bg: "#f1f8f2",
    text: "#1b5e20",
    chipBg: "#2e7d32",
    chipText: "#fff",
  },
  fail: {
    label: "Fail",
    border: "#d32f2f",
    bg: "#fdf2f2",
    text: "#b71c1c",
    chipBg: "#d32f2f",
    chipText: "#fff",
  },
  error: {
    label: "Error",
    border: "#e65100",
    bg: "#fff4e5",
    text: "#bf360c",
    chipBg: "#e65100",
    chipText: "#fff",
  },
  neutral: {
    label: "Info",
    border: "#90a4ae",
    bg: "#f5f7f8",
    text: "#37474f",
    chipBg: "#eceff1",
    chipText: "#37474f",
  },
};

const MUTED = "#9e9e9e";

const PASS_OUTCOME_NAMES = [
  "All Passed",
  "Passed",
  "Normal Report",
  "Atleast one passed",
  "In Range",
  "Success",
  "Medications Present",
  "Inside QRisk Score",
  "In Age Range",
];

const FAIL_OUTCOME_NAMES = [
  "Failed",
  "All Failed",
  "Out of Range",
  "Medications Not Present",
  "Outside QRisk Score",
  "Outside Age Range",
];

const FAILED_OUTCOME_NAMES = new Set(
  FAIL_OUTCOME_NAMES.map((n) => n.toLowerCase()),
);

const isFailedOutcome = (name) =>
  FAILED_OUTCOME_NAMES.has(String(name).trim().toLowerCase());

const OUTCOME_REGEX_SOURCE = [...PASS_OUTCOME_NAMES, ...FAIL_OUTCOME_NAMES]
  .sort((a, b) => b.length - a.length)
  .map(escapeRegex)
  .join("|");

const CLASSIFIERS = [
  { tone: "error", re: /^Exception\b/i },
  { tone: "error", re: /^Filing patient\.\s*Error/i },
  { tone: "error", re: /\bError\s+-\s/i },
  { tone: "error", re: /^Protocol Name not defined/i },
  {
    tone: "error",
    re: /^Protocol output (?:empty|doesnt match|doesn't match)/i,
  },
  { tone: "error", re: /^Test '.*' not found in the report/i },
  { tone: "error", re: /^No value found for the Subtest/i },
  { tone: "error", re: /^Previous value not found/i },
  { tone: "error", re: /Invalid previous_x type/i },
  { tone: "error", re: /doesn't match required emis id/i },
  { tone: "fail", re: /^Not Filed\b/i },
  { tone: "fail", re: /\bnot within\b/i },
  { tone: "fail", re: /out of acceptable range/i },
  { tone: "fail", re: /^Trend break detected/i },
  { tone: "fail", re: /outside qrisk score/i },
  { tone: "fail", re: /^Unable to add code/i },
  { tone: "neutral", re: /Outcomes Received/i },
  { tone: "neutral", re: /^previous_x_value must be/i },
  { tone: "neutral", re: /already been filed/i },
  { tone: "neutral", re: /^No (?:usable )?previous results found/i },
  { tone: "neutral", re: /^Subtest label '.*' not found/i },
  { tone: "neutral", re: /^Not able to get previous range data/i },
  { tone: "neutral", re: /^Qrisk Percentage is [\d.]+%?\s*$/i },
  { tone: "neutral", re: /^Test '.*' found with value/i },
  { tone: "neutral", re: /^Not Applicable/i },
  { tone: "pass", re: /^(?:Dry Run|Intent once approved)\b/i },
  { tone: "pass", re: /^Launch Protocol\b/i },
  { tone: "pass", re: /\bwithin\b/i },
  { tone: "pass", re: /in acceptable range/i },
  { tone: "pass", re: /follow an acceptable trend/i },
  { tone: "pass", re: /inside qrisk score/i },
  {
    tone: "pass",
    re: /^(?:Going to|Filed report|Task created|Owner changed|Added code|Code\s-)/i,
  },
  { tone: "pass", re: /added to the record/i },
];

const classifyMessage = (text) => {
  const hit = CLASSIFIERS.find((c) => c.re.test(text));
  return hit ? hit.tone : null;
};

// Prefixes that describe *how* the action ran rather than what happened
const MODE_PREFIXES = [
  { re: /^Dry Run\s*-\s*/i, label: "Dry run" },
  { re: /^Intent once approved\s*-\s*/i, label: "Pending approval" },
];

/**
 * Turns a viewer line into { label, tone, mode, body }.
 * Uses node.msg (the raw backend message) when present, because lineText
 * prepends the node name. Falls back to line.text for template-only lines.
 */
const parseLine = (line) => {
  const msg = line.node?.msg;
  let label = "";
  let body;

  if (msg !== undefined && msg !== null) {
    label = safeText(line.node?.node);
    body = safeText(msg);
    // Avoid "Launch Protocol - Launch Protocol - X"
    if (label && body.startsWith(`${label} - `)) {
      body = body.slice(label.length + 3);
    }
  } else {
    body = safeText(line.text);
  }

  const tone = classifyMessage(body);

  let mode = null;
  for (const p of MODE_PREFIXES) {
    if (p.re.test(body)) {
      mode = p.label;
      body = body.replace(p.re, "");
      break;
    }
  }

  return { label, tone, mode, body };
};

/*                                                                            */
/*  Each rule is applied in order to the still-unstyled parts of the text.    */
/*  - `style`      : "good" | "bad" | "bold", or a function(matchedText)      */
/*                   returning one of those                                   */
/*  - `valueGroup` : when true, regex group 1 stays plain and group 2 is      */
/*                   styled (used for "label - VALUE" patterns).              */

const HIGHLIGHT_RULES = [
  // Category name before "- Outcomes Received", e.g. "BCS: FOB result"
  {
    re: /(^|\s-\s)((?:(?!\s-\s).)+?)(?=\s+-\s+Outcomes Received)/g,
    style: "bold",
    valueGroup: true,
  },

  // Values that follow a label: names, codes, owners
  { re: /(for clinician\s+)(.+)$/gi, style: "bold", valueGroup: true },
  { re: /(owner to(?:\s-)?\s+)(.+)$/gi, style: "bold", valueGroup: true },
  { re: /(Change Owner\s-\s)(.+)$/gi, style: "bold", valueGroup: true },
  {
    re: /((?:add code|added code|unable to add code|code)\s*-\s*)(.+?)(?=\s*,\s*code not found|\s+added to the record|$)/gi,
    style: "bold",
    valueGroup: true,
  },
  { re: /(Launch Protocol\s-\s)(.+)$/g, style: "bold", valueGroup: true },
  { re: /(Skip NHS Number\)\s-\s)(.+)$/g, style: "bold", valueGroup: true },

  // Outcome names anywhere in a message, e.g. Outcomes Received 'All Passed'.
  {
    re: new RegExp(`\\b(?:${OUTCOME_REGEX_SOURCE})\\b`, "gi"),
    style: (matched) => (isFailedOutcome(matched) ? "bad" : "good"),
  },

  // Quoted names such as 'Haemoglobin'
  { re: /'[^']*'/g, style: "bold" },

  // Negative phrases
  {
    re: /\b(not within|out of acceptable range|outside qrisk score|trend break detected|unable to add code|code not found|no value found|not found|not filed|doesn't match|doesnt match|is empty|empty|not defined|invalid)\b/gi,
    style: "bad",
  },

  // Positive phrases
  {
    re: /\b(within|in acceptable range|inside qrisk score|follow an acceptable trend|filed report|task created|owner changed|added code|added to the record|passed by default|trend check passed)\b/gi,
    style: "good",
  },

  // Actions about to happen
  {
    re: /\b(going to (?:file report|send accurx message|create task|change owner|add code))\b/gi,
    style: "bold",
  },

  // "Subtest X" and similar labels
  {
    re: /\b(Subtest\s+\S+|Patient age|Previous value|Qrisk Percentage)\b/g,
    style: "bold",
  },

  // Numbers and percentages (values, ranges, drop/gain)
  { re: /-?\d+(?:\.\d+)?%?/g, style: "bold" },
];

const resolveStyle = (rule, matched) =>
  typeof rule.style === "function" ? rule.style(matched) : rule.style;

const applyRule = (segments, rule) => {
  const out = [];

  segments.forEach((seg) => {
    if (seg.style) {
      out.push(seg);
      return;
    }

    const text = seg.t;
    let last = 0;

    for (const m of text.matchAll(rule.re)) {
      if (m[0] === "") continue;
      if (m.index > last) out.push({ t: text.slice(last, m.index) });

      if (rule.valueGroup) {
        if (m[1]) out.push({ t: m[1] });
        out.push({ t: m[2], style: resolveStyle(rule, m[2]) });
      } else {
        out.push({ t: m[0], style: resolveStyle(rule, m[0]) });
      }
      last = m.index + m[0].length;
    }

    if (last < text.length) out.push({ t: text.slice(last) });
  });

  return out;
};

const highlight = (text) => HIGHLIGHT_RULES.reduce(applyRule, [{ t: text }]);

const segmentSx = (style, muted) => {
  if (style === "good") {
    return { fontWeight: 700, color: muted ? "inherit" : "#2e7d32" };
  }
  if (style === "bad") {
    return { fontWeight: 700, color: muted ? "inherit" : "#c62828" };
  }
  return { fontWeight: 700 };
};

const HighlightedText = ({ text, muted }) => (
  <>
    {highlight(text).map((seg, i) =>
      seg.style ? (
        <Box component="span" key={i} sx={segmentSx(seg.style, muted)}>
          {seg.t}
        </Box>
      ) : (
        <React.Fragment key={i}>{seg.t}</React.Fragment>
      ),
    )}
  </>
);

const PAIRS_RE = /^([\s\S]*?)\s+—\s+([\s\S]+)$/;

const MessageBody = ({ body, muted }) => {
  const pairMatch = body.match(PAIRS_RE);

  if (pairMatch && pairMatch[2].includes(";")) {
    const items = pairMatch[2]
      .split(";")
      .map((s) => s.trim())
      .filter(Boolean);

    return (
      <>
        <HighlightedText text={pairMatch[1]} muted={muted} />
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
          {items.map((item, i) => {
            const kv = item.match(/^([^:=]+?)\s*([:=])\s*([\s\S]*)$/);
            return (
              <li key={i}>
                {kv ? (
                  <>
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {kv[1]}
                    </Box>
                    {`${kv[2]} `}
                    <HighlightedText text={kv[3]} muted={muted} />
                  </>
                ) : (
                  <HighlightedText text={item} muted={muted} />
                )}
              </li>
            );
          })}
        </Box>
      </>
    );
  }

  return <HighlightedText text={body} muted={muted} />;
};

const InactiveTag = () => (
  <Typography
    component="span"
    variant="caption"
    sx={{ ml: 1, color: MUTED, fontStyle: "italic" }}
  >
    (Inactive)
  </Typography>
);

const MessageLine = ({ line, isSelected, deactivated }) => {
  const { label, tone, mode, body } = parseLine(line);
  const t = tone ? TONES[tone] : null;

  // Unclassified lines: plain look, but still highlighted
  if (!t) {
    return (
      <Typography
        variant="body2"
        sx={{
          fontSize: "0.9rem",
          color: deactivated ? MUTED : isSelected ? "#1e88e5" : "inherit",
          whiteSpace: "normal",
          wordBreak: "break-word",
        }}
      >
        {label && (
          <Box component="span" sx={{ fontWeight: 700 }}>
            {label}
            {body ? " - " : ""}
          </Box>
        )}
        <MessageBody body={body} muted={deactivated} />
        {deactivated && <InactiveTag />}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        borderLeft: `3px solid ${deactivated ? "#bdbdbd" : t.border}`,
        backgroundColor: deactivated ? "transparent" : t.bg,
        outline: isSelected ? "1px solid #1e88e5" : "none",
      }}
    >
      {/*
      <Chip
        size="small"
        label={t.label}
        sx={{
          height: 20,
          fontSize: "0.7rem",
          fontWeight: 700,
          flexShrink: 0,
          mt: "2px",
          color: deactivated ? MUTED : t.chipText,
          backgroundColor: deactivated ? "#eeeeee" : t.chipBg,
        }}
      />
	 */}
      <Typography
        variant="body2"
        component="div"
        sx={{
          fontSize: "0.9rem",
          color: deactivated ? MUTED : t.text,
          whiteSpace: "normal",
          wordBreak: "break-word",
          flex: 1,
        }}
      >
        {label && (
          <Box component="span" sx={{ fontWeight: 700 }}>
            {label}
            {" · "}
          </Box>
        )}

        {mode && (
          <Chip
            size="small"
            variant="outlined"
            label={mode}
            sx={{
              height: 18,
              mr: 0.75,
              fontSize: "0.65rem",
              fontWeight: 600,
              color: "inherit",
              borderColor: "currentColor",
            }}
          />
        )}

        <MessageBody body={body} muted={deactivated} />

        {deactivated && <InactiveTag />}
      </Typography>
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/*  Node error (line.node.error) - same layout as LineMessage, red styling    */
/* -------------------------------------------------------------------------- */

const ERROR_STYLE = {
  border: "#d32f2f",
  bg: "#fdf2f2",
  text: "#b71c1c",
};

const ErrorMessage = ({ label, error, deactivated, isSelected }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1,
      mt: 0.5,
      px: 1,
      py: 0.5,
      borderRadius: 1,
      borderLeft: `3px solid ${deactivated ? "#bdbdbd" : ERROR_STYLE.border}`,
      backgroundColor: deactivated ? "transparent" : ERROR_STYLE.bg,
      outline: isSelected ? "1px solid #1e88e5" : "none",
    }}
  >
    <Typography
      variant="body2"
      component="div"
      sx={{
        fontSize: "0.9rem",
        color: deactivated ? MUTED : ERROR_STYLE.text,
        whiteSpace: "normal",
        wordBreak: "break-word",
        flex: 1,
      }}
    >
      {label && (
        <Box component="span" sx={{ fontWeight: 700 }}>
          {label}
          {" - "}
        </Box>
      )}
      {/* muted=true keeps the bold highlighting but drops green/red so the block stays red */}
      <MessageBody body={safeText(error)} muted />
      {deactivated && <InactiveTag />}
    </Typography>
  </Box>
);

/**
 * Renders a node's message and/or error.
 * - Error only (no msg):  one red line  -> "Check Range - <error text>"
 * - Msg and error:        the normal message line, then the red error line below it
 * - Msg only:             the normal message line
 */
const LineMessage = ({ line, isSelected, deactivated }) => {
  const errorText = line.node?.error ? safeText(line.node.error) : "";
  const label = safeText(line.node?.node);
  const rawMsg = line.node?.msg;
  const hasMsg =
    rawMsg !== undefined && rawMsg !== null && safeText(rawMsg).trim() !== "";

  if (errorText && !hasMsg) {
    return (
      <ErrorMessage
        label={label}
        error={errorText}
        deactivated={deactivated}
        isSelected={isSelected}
      />
    );
  }

  return (
    <>
      <MessageLine
        line={line}
        isSelected={isSelected}
        deactivated={deactivated}
      />
      {errorText && (
        <ErrorMessage
          label={label}
          error={errorText}
          deactivated={deactivated}
        />
      )}
    </>
  );
};

/* -------------------------------------------------------------------------- */
/*  Outcome header ("Outcome of Rule 1.2 - All Passed")                       */
/* -------------------------------------------------------------------------- */

const OutcomeHeader = ({ text }) => {
  const m = text.match(/^(.*) - ([^-]+)$/);

  if (!m) {
    return (
      <Typography variant="caption" sx={{ color: "#6c63ff", fontWeight: 600 }}>
        {text}
      </Typography>
    );
  }

  const outcome = m[2].trim();
  const color = isFailedOutcome(outcome) ? "#c62828" : "#2e7d32";

  return (
    <Typography variant="caption" sx={{ color: "#6c63ff" }}>
      {m[1]}
      {" - "}
      <Box component="span" sx={{ fontWeight: 700, color }}>
        {outcome}
      </Box>
    </Typography>
  );
};

/* -------------------------------------------------------------------------- */
/*  Component                                                                 */
/* -------------------------------------------------------------------------- */

const NodeViewer = ({
  root,
  selectedCategory = null,
  nodeConfigs = {},
  onSelectNode,
  selectedNodeId,
  showTooltip = true,
}) => {
  let lines = [];

  try {
    lines = buildNumberedList(root, nodeConfigs);
  } catch (error) {
    console.error("NodeViewer: buildNumberedList failed", error);
    console.error("Root:", root);

    return (
      <Alert severity="error">
        <AlertTitle>Unable to display ruleset</AlertTitle>

        <Typography variant="body2">
          The ruleset contains data that could not be rendered.
        </Typography>

        <Box
          component="pre"
          sx={{
            mt: 1,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: "0.75rem",
          }}
        >
          {safeText(error?.message || error)}
        </Box>
      </Alert>
    );
  }

  if (!Array.isArray(lines)) {
    console.error("NodeViewer: buildNumberedList returned:", lines);

    return (
      <Alert severity="error">
        <AlertTitle>Invalid ruleset</AlertTitle>
        <Typography variant="body2">
          The ruleset could not be converted into displayable rules.
        </Typography>
      </Alert>
    );
  }

  return (
    <Paper
      sx={{
        p: 1.5,
        mb: 1.5,
        background: "#f9f9ff",
        border: "1px solid #e0e0e0",
      }}
      elevation={1}
    >
      <Box sx={{ mb: 2, p: 1 }}>
        {lines.map((line, index) => {
          if (!line) {
            return null;
          }

          if (line.isOutcomeHeader) {
            return (
              <Box
                key={`viewer-header-${line.id ?? index}`}
                sx={{
                  ml: (line.depth || 0) * 2,
                  my: 0.5,
                }}
              >
                <OutcomeHeader text={safeText(line.text)} />
              </Box>
            );
          }

          const isSelected = selectedNodeId === line.id;
          const deactivated = Boolean(line.node?.fields?.deactivated);

          const ruleButton = (
            <Button
              size="small"
              onClick={() =>
                onSelectNode &&
                onSelectNode(
                  isSelected
                    ? { id: null, numbering: null }
                    : { id: line.id, numbering: line.numbering },
                )
              }
              variant={isSelected ? "contained" : "outlined"}
              sx={{
                minWidth: 32,
                width: "fit-content",
                height: 28,
                fontSize: "0.8rem",
                lineHeight: 1,
                whiteSpace: "nowrap",
                flexShrink: 0,
                ...(deactivated && {
                  color: "#9e9e9e",
                  borderColor: "#bdbdbd",
                  backgroundColor: isSelected ? "#e0e0e0" : "transparent",
                  textDecoration: "line-through",
                }),
              }}
            >
              Rule - {safeText(line.numbering) || "•"}
            </Button>
          );

          return (
            <Box
              key={`viewer-${line.id ?? index}`}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                ml: Math.max(0, line.depth || 0) * 2,
                py: 0.5,
              }}
            >
              {showTooltip ? (
                <Tooltip title="Click to focus this rule in the editor" arrow>
                  {ruleButton}
                </Tooltip>
              ) : (
                ruleButton
              )}

              <Box sx={{ flex: 1 }}>
                <LineMessage
                  line={line}
                  isSelected={isSelected}
                  deactivated={deactivated}
                />

                {line.node?.traceback?.traceback && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1.5,
                      backgroundColor: "#fff3e0",
                      border: "1px solid #ff9800",
                      borderRadius: 1,
                      maxWidth: "100%",
                      overflow: "auto",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: "#e65100",
                        display: "block",
                        mb: 0.5,
                      }}
                    >
                      Traceback:
                    </Typography>

                    <Typography
                      component="pre"
                      variant="body2"
                      sx={{
                        fontSize: "0.75rem",
                        fontFamily: "monospace",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        color: "#d84315",
                        margin: 0,
                      }}
                    >
                      {safeText(line.node.traceback.traceback)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export default NodeViewer;
